import React from 'react';
import { Desc, Heading, Title } from './index.styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { styled as styledMUI } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  ArticleOutlined,
  BadgeOutlined,
  Inventory2Outlined,
  PlaceOutlined,
  PrecisionManufacturingOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';
import BasicInfo from './forms/basic-info';
import Location from './forms/location';
import { useTranslation } from 'react-i18next';
import AdditionalInfo from './forms/additional-info';
import Certifications from './forms/certifications';
import Suppliers from './forms/suppliers';
import TechnicalSpecifications from './forms/technical-specifications';
import SectionCard from './SectionCard';
import { FieldInfoIcon } from 'src/components/FieldInfoIcon';

const StyledTextFieldBase = styledMUI(TextField)<TextFieldProps>`
& .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: #7E4308;
    };
  };
  & label.Mui-focused {
    color: #7E4308;
  }
`;

type StyledLcaTextFieldProps = TextFieldProps & {
  infoText?: React.ReactNode;
  showInfoIcon?: boolean;
};

export function StyledTextField({
  InputProps,
  infoText,
  label,
  showInfoIcon = true,
  ...props
}: StyledLcaTextFieldProps) {
  const { t } = useTranslation('lca');
  const resolvedInfoText =
    infoText ??
    (typeof label === 'string'
      ? t('fieldInfoFallback', { field: label })
      : t('fieldInfoFallbackGeneric'));

  return (
    <StyledTextFieldBase
      {...props}
      label={label}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            {showInfoIcon && <FieldInfoIcon title={resolvedInfoText} />}
          </>
        ),
      }}
    />
  );
}

export interface ProductInformationProps {
  values: {
    id: number;
    name: string;
    description: string;
    development_flowcharts_description: string;
    related_documents_description: string;
    technical_documentation_description: string;
    manufacturer_name: string;
    importer_name: string;
    key_company_activities: string;
    regulatory_frameworks: string;
    product_category_rules: string;
    revision: string;
    lca_preparation_date: string;
    valid_until: string;
    production_location: string;
    production_country: { iso_code: string };
    nace_industries: Array<{
      code: string;
    }>;
    distribution_regions: Array<{
      iso_code: string;
      name: string;
    }>;
    cpc_industry: { code: string };
    ecoinvent_version: string | null;
    image_id: string | null;
    passport_qr_image_id: string | null;
    passport_qr_target_url: string;
    passport_access_token?: string;
    gtin_code: string;
    gpc_code: string | null;
    ce_mark: boolean;
    model_batch_serial_number: string;
    availability_of_spare_parts: string;
    repair_disassembly_instructions_description: string;
    recycling_possibilities: string;
    disassembly_instructions: string;
    epr_information_description: string;
    guaranteed_service_life: string;
    operating_life: string;
    mean_time_between_failures: string;
    actual_product_use_information: string;
    resistance_to_stress_or_aging_mechanisms: string;
    product_type: string;
    production_date: string | null;
    warranty_period_conditions: string;
    suppliers: string;
    technical_specifications: string;
    additional_information: string;
  };
  setValues: Function;
  basicInfoOnly?: boolean;
}

export default function ProductInformation({
  setValues,
  values,
  basicInfoOnly = false,
}: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'productInformation' });
  const leftColumnSections = [
    {
      key: 'basicInformation',
      title: t('basicInformation'),
      subtitle: t('sectionDescriptions.basicInformation'),
      icon: <Inventory2Outlined sx={{ color: '#7e4308' }} />,
      content: <BasicInfo values={values} setValues={setValues} />,
    },
    {
      key: 'additionalInformation',
      title: t('additionalInformation'),
      subtitle: t('sectionDescriptions.additionalInformation'),
      icon: <ArticleOutlined sx={{ color: '#7e4308' }} />,
      content: <AdditionalInfo values={values} setValues={setValues} />,
    },
  ];

  const rightColumnSections = [
    {
      key: 'distributionProducerCountryOfProduction',
      title: t('distributionProducerCountryOfProduction'),
      subtitle: t(
        'sectionDescriptions.distributionProducerCountryOfProduction'
      ),
      icon: <PlaceOutlined sx={{ color: '#7e4308' }} />,
      content: <Location values={values} setValues={setValues} />,
    },
    {
      key: 'keySuppliers',
      title: t('keySuppliers'),
      subtitle: t('sectionDescriptions.keySuppliers'),
      icon: <BadgeOutlined sx={{ color: '#7e4308' }} />,
      content: <Suppliers values={values} setValues={setValues} />,
    },
    {
      key: 'technicalSpecifications',
      title: t('technicalSpecifications'),
      subtitle: t('sectionDescriptions.technicalSpecifications'),
      icon: <PrecisionManufacturingOutlined sx={{ color: '#7e4308' }} />,
      content: (
        <TechnicalSpecifications values={values} setValues={setValues} />
      ),
    },
    {
      key: 'certifications',
      title: t('certifications'),
      subtitle: t('sectionDescriptions.certifications'),
      icon: <VerifiedOutlined sx={{ color: '#7e4308' }} />,
      content: <Certifications values={values} setValues={setValues} />,
    },
  ];

  return (
    <div>
      <Title>{t('title')}</Title>
      {!basicInfoOnly && <Desc>{t('desc')}</Desc>}

      {basicInfoOnly && (
        <>
          <Heading>{t('basicInformation')}</Heading>
          <BasicInfo values={values} setValues={setValues} />
        </>
      )}

      {!basicInfoOnly && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' },
            gap: '24px',
            mt: '40px',
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {leftColumnSections.map((section) => (
              <SectionCard
                key={section.key}
                icon={section.icon}
                title={section.title}
                subtitle={section.subtitle}
              >
                {section.content}
              </SectionCard>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {rightColumnSections.map((section) => (
              <SectionCard
                key={section.key}
                icon={section.icon}
                title={section.title}
                subtitle={section.subtitle}
              >
                {section.content}
              </SectionCard>
            ))}
          </Box>
        </Box>
      )}
    </div>
  );
}
